// src/components/LiveScheduleView.jsx
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Import React Icons
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import { HiArrowsPointingOut } from 'react-icons/hi2';
import { HiArrowsPointingIn } from 'react-icons/hi2';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';
import { IoMdMicrophone } from 'react-icons/io';
import { FaRegClock } from 'react-icons/fa';
import { isStudentsBuild, isTeachersBuild, isAdminBuild, isLandingBuild, isPublicFacingBuild } from '../config/AppStatic';
import Button from './ui/Button';
import { useDeviceLayout } from '../contexts/DeviceLayoutContext';
import DayTableSimple from './DayTableSimple.tsx';
import DayTableFit from './DayTableFit.tsx';
import ScheduleFilter from './ScheduleFilter';
import StudentGroupSelectionModal from './StudentGroupSelectionModal';
import WeekNavigator from './WeekNavigator';
import HelpIcon from './Documentation/HelpIcon'; // Import the help icon component
import ComplexFilterModal from './ComplexFilterModal'; // Import the complex filter modal
import ScheduleEntryDetailView from './ScheduleEntryDetailView';
import { SCHEDULE_FILTER_ALL_GROUPS, isScheduleFilterAllGroupsOnly } from '../config/constants';
import { getUniqueRooms, getWeekStartDate, formatWeekLabel, getWeekNumber, getActiveDayKeyForTodayInDisplayedWeek } from '../utils/helpers.ts';
import LinearWeekViewDOM from './LinearWeekViewDOM';
import { useActiveDays } from '../hooks/useActiveDays';
import SchoolHeader from './SchoolHeader';
import FixedDayHeader from './common/FixedDayHeader';
import EventContainer from './EventContainer';
import ScheduleEventsView from './ScheduleEventsView';
import RoomsOccupancyView from './RoomsOccupancyView';
import SchedulePrimaryViewDrawer from './SchedulePrimaryViewDrawer';
import { clearLastInstitutionUrlEndpoint } from '../utils/lastInstitutionUrlEndpoint';
import AssetManager from '../services/AssetManager.ts';
import PeriodManager from '../services/PeriodManager.ts';
import AcademicYearService from '../services/AcademicYearService.ts';
import { deriveScheduleTimelineContext } from '../utils/scheduleContext';
import { SharedScrollProvider } from '../contexts/SharedScrollContext';
import { isDemoLoggedIn } from '../services/authService';
import { getWeekDateRange } from '../utils/timeUtils';

const A4_ASPECT_RATIO = 1.41;

const summarizeScheduleEntriesForLog = (entries) => {
    const safeEntries = Array.isArray(entries) ? entries.filter(Boolean) : [];
    const firstFew = safeEntries.slice(0, 5).map(entry => ({
        id: entry?.id,
        day: entry?.day,
        weekSeries: entry?.weekSeries || '',
        start: entry?.timeStart || entry?.start_time || '',
        end: entry?.timeEnd || entry?.end_time || '',
        room: entry?.room || entry?.room_name || '',
        course: entry?.course || entry?.subject || entry?.subject_name || '',
    }));

    return {
        count: safeEntries.length,
        sample: firstFew,
    };
};

/** Parse schedule filter query params (?teacher= & ?groups= & ?course=) without React hooks. */
function parseScheduleFilterParamsFromSearch(search) {
    const sp = new URLSearchParams(search || '');
    const teacher = (sp.get('teacher') || '').trim();
    const groupsRaw = (sp.get('groups') || '').trim();
    const course = (sp.get('course') || '').trim();
    const groups = groupsRaw ? groupsRaw.split(',').map(g => g.trim()).filter(Boolean) : [];
    return { teacher, groups, course };
}

const ExpandIcon = () => <HiArrowsPointingOut className="h-full w-full" />;
const FitIcon = () => <HiArrowsPointingIn className="h-full w-full" />;

const LIVE_SCHEDULE_LOOKAHEAD_MONTHS = 3;

const getPeriodActiveDraftId = (period) => {
    const raw = period?.activeScheduleDraftId ?? period?.active_schedule_draft_id;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getLiveScheduleDateRange = () => {
    const rangeStart = new Date();
    const rangeEnd = new Date(rangeStart);
    rangeEnd.setMonth(rangeEnd.getMonth() + LIVE_SCHEDULE_LOOKAHEAD_MONTHS);
    return { rangeStart, rangeEnd };
};

const getMergedScheduleEntryKey = (entry) => {
    const additionalProps = entry?.additionalProps || {};
    const draftId = additionalProps.schedule_draft_id ?? entry?.schedule_draft_id ?? entry?.scheduleDraftId ?? 'live';
    const periodId = entry?.periodId ?? entry?.period_id ?? entry?.periodRef ?? 'global';
    // Prefer database id — publishIdentity can be shared by distinct entries (e.g. duplicated draft rows).
    const stableId = (entry?.id != null && entry?.id !== '')
        ? String(entry.id)
        : (entry?.publishIdentity || `${entry?.day || ''}|${entry?.weekSeries || ''}|${entry?.timeStart || ''}|${entry?.timeEnd || ''}`);
    return `${draftId}|${periodId}|${stableId}`;
};

const mergeScheduleEntries = (entryGroups) => {
    const merged = [];
    const seen = new Set();

    entryGroups.flat().filter(Boolean).forEach((entry) => {
        const key = getMergedScheduleEntryKey(entry);
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        merged.push(entry);
    });

    return merged;
};

/** LiveScheduleView is read-only; drag hooks are not used but DayTable* still expect these shapes. */
const EMPTY_DRAGGED_ITEM_INFO = { entry: null, clickOffsetY: 0, durationMinutes: 0 };
const KEY_SEPARATOR = '|';
const noop = () => { };

function LiveScheduleView({
    user,
    teachers,
    groups,
    subjects,
    rooms,
    /* NEW: filters and include if passed from InstitutionalSchedulePage */
    filters: propFilters,
    include: propInclude,
    isPublicView = false,
    isScheduleShare = false,
    scheduleShareMeta = null,
    scheduleShareSlug = '',
    forcedScheduleEntries = null,
    isAssetsLoading = false,
    onAddTeacher,
    onAddSubject,
    onAddGroup
}) {
    // Schedule shares / forced preview entries always refetch (or skip network when forced).
    const hasForcedEntries = Array.isArray(forcedScheduleEntries);
    const showScheduleShareBanner = isScheduleShare && !hasForcedEntries;
    const scheduleEntriesFetchOptions = useMemo(
        () => ({ forceNetwork: isScheduleShare && !hasForcedEntries ? true : false }),
        [isScheduleShare, hasForcedEntries]
    );
    /* PERFORMANCE OPTIMIZATIONS APPLIED:
     * 1. Split massive filtering effect into smaller, focused effects and memoized computations
     * 2. Memoized expensive date/week calculations to prevent recalculation on every render
     * 3. Debounced holiday loading to prevent excessive API calls
     * 4. Added useCallback to prevent unnecessary re-renders of child components
     * 5. Moved expensive filtering logic to useMemo hooks for better performance
     */

    const { isMobileLayout } = useDeviceLayout();

    // Initialize URL state management
    const location = useLocation();
    const navigate = useNavigate();
    const shouldSyncFiltersToUrl = !(isPublicView && isLandingBuild());
    const shouldPersistPublicFilters = isPublicView && !isLandingBuild();

    const initialCachedScheduleEntries = hasForcedEntries
        ? forcedScheduleEntries
        : (isScheduleShare
            ? AssetManager.getInstance().getCachedScheduleEntries(propFilters || {}, propInclude || '')
            : null);
    const initialCachedScheduleItems = initialCachedScheduleEntries || [];
    const hasInitialCachedSchedule = initialCachedScheduleEntries !== null;
    const schedulePipelineLogRef = useRef('');

    // Get active days from AssetManager
    const { activeDays, loading: daysLoading } = useActiveDays();

    // --- State variables ---
    // Initialize filters from URL on first paint so children (e.g. ScheduleFilter auto-modal) never see a false "no group" frame before the mount effect runs.
    const [baseScheduleItems, setBaseScheduleItems] = useState(initialCachedScheduleItems);
    const [selectedTeacher, setSelectedTeacher] = useState(() =>
        parseScheduleFilterParamsFromSearch(location.search).teacher
    );
    const [selectedCourse, setSelectedCourse] = useState(() =>
        parseScheduleFilterParamsFromSearch(location.search).course
    );
    const [selectedGroups, setSelectedGroups] = useState(() =>
        parseScheduleFilterParamsFromSearch(location.search).groups
    );
    const [isFilteredView, setIsFilteredView] = useState(false);

    // Complex filter state
    const [isComplexFilterModalOpen, setIsComplexFilterModalOpen] = useState(false);
    const [complexFilterGroups, setComplexFilterGroups] = useState(() => {
        const g = parseScheduleFilterParamsFromSearch(location.search).groups;
        return g.length > 0 ? [...g] : [];
    });
    const [complexFilterTeachers, setComplexFilterTeachers] = useState(() => {
        const t = parseScheduleFilterParamsFromSearch(location.search).teacher;
        return t ? [t] : [];
    });
    const [complexFilterSubjects, setComplexFilterSubjects] = useState(() => {
        const c = parseScheduleFilterParamsFromSearch(location.search).course;
        return c ? [c] : [];
    });
    const [complexFilterRooms, setComplexFilterRooms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dataInitialized, setDataInitialized] = useState(hasInitialCachedSchedule); // New state to track initial data load
    /** Live-schedule version sync message (shown briefly after load; not used for schedule-share views). */
    const [scheduleSyncFeedback, setScheduleSyncFeedback] = useState(null);

    // Add new state for week series and year
    const [weekSeries, setWeekSeries] = useState('');
    const [currentDate, setCurrentDate] = useState(new Date()); // State for current date

    useEffect(() => {
        if (!showScheduleShareBanner || !scheduleShareMeta) return;
        const range = getWeekDateRange(
            Number(scheduleShareMeta?.startWeekYear),
            Number(scheduleShareMeta?.startWeek)
        );
        if (range?.startDate) {
            setCurrentDate(range.startDate);
        }
    }, [showScheduleShareBanner, scheduleShareMeta, scheduleShareMeta?.startWeek, scheduleShareMeta?.startWeekYear]);

    const todayWideDayKey = useMemo(
        () => getActiveDayKeyForTodayInDisplayedWeek(currentDate, activeDays),
        [currentDate, activeDays]
    );

    // New: time view mode and academic periods (semester mode)
    const [timeMode, setTimeMode] = useState('weeks'); // 'weeks' | 'semester' | 'occupancy' | 'events'
    const [lastCalendarMode, setLastCalendarMode] = useState('weeks'); // remember last non-occupancy mode
    const [periods, setPeriods] = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    const [eventPanelEntryType, setEventPanelEntryType] = useState('event'); // 'event' | 'meeting'
    const [isEventPanelCollapsed, setIsEventPanelCollapsed] = useState(false);
    // Left drawer for primary view selection (Calendar vs Rooms Occupancy)
    const [isLeftDrawerOpen, setIsLeftDrawerOpen] = useState(false);
    const [isLeftDrawerAnimating, setIsLeftDrawerAnimating] = useState(false);

    // --- URL State Management ---
    // This section handles URL parameter synchronization for filter state.
    // URL parameters supported:
    // - ?teacher=TeacherName - Filter by specific teacher
    // - ?groups=Group1,Group2 - Filter by specific groups (comma-separated)
    // - ?course=CourseName - Filter by specific course
    // 
    // Examples:
    // - /institution/schedule?teacher=John%20Doe
    // - /institution/schedule?groups=Class%20A,Class%20B
    // - /institution/schedule?teacher=Jane%20Smith&groups=Advanced%20Class
    //
    // Helper function to parse URL parameters and initialize filter state
    const parseUrlParams = useCallback(() => {
        return parseScheduleFilterParamsFromSearch(location.search);
    }, [location.search]);

    // Helper function to update URL with current filter state
    const updateUrlWithFilters = useCallback((teacher, groups, course) => {
        if (!shouldSyncFiltersToUrl) return;

        const searchParams = new URLSearchParams(location.search);

        // Update or remove parameters based on current values
        if (teacher && teacher.trim()) {
            searchParams.set('teacher', teacher.trim());
        } else {
            searchParams.delete('teacher');
        }

        if (groups && groups.length > 0) {
            // Filter out empty strings and trim whitespace
            const validGroups = groups.filter(g => g && g.trim()).map(g => g.trim());
            if (validGroups.length > 0) {
                searchParams.set('groups', validGroups.join(','));
            } else {
                searchParams.delete('groups');
            }
        } else {
            searchParams.delete('groups');
        }

        if (course && course.trim()) {
            searchParams.set('course', course.trim());
        } else {
            searchParams.delete('course');
        }

        // Update URL without triggering a page reload
        const newSearch = searchParams.toString();
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

        // Only update if the URL actually changed to prevent unnecessary navigation
        if (newUrl !== location.pathname + location.search) {
            navigate(newUrl, { replace: true });
        }
    }, [location.pathname, location.search, navigate, shouldSyncFiltersToUrl]);

    // Initialize filter state from URL parameters on mount (simple + complex modal stay aligned)
    useEffect(() => {
        const urlParams = parseUrlParams();

        // Log URL parameters for debugging
        if (urlParams.teacher || urlParams.groups.length > 0 || urlParams.course) {
            console.log('[LiveScheduleView] Initializing filters from URL:', urlParams);
        }

        // Always respect URL parameters on initial load
        // This allows direct URL access to filtered views
        setSelectedTeacher(urlParams.teacher);
        setSelectedGroups(urlParams.groups);
        setSelectedCourse(urlParams.course);
        // Mirror URL into advanced filter state so ComplexFilterModal matches what is actually applied
        setComplexFilterGroups(urlParams.groups.length > 0 ? [...urlParams.groups] : []);
        setComplexFilterTeachers(urlParams.teacher ? [urlParams.teacher] : []);
        setComplexFilterSubjects(urlParams.course ? [urlParams.course] : []);
        setComplexFilterRooms([]);
    }, []); // Only run on mount

    // Persist last-used public teacher/group filter and restore when visiting without query params
    const lastPublicFilterKey = useMemo(() => `publicLastLinearFilter:${location.pathname}`, [location.pathname]);

    // Restore saved filter on initial load when in public view and no URL filters are present
    useEffect(() => {
        if (!shouldPersistPublicFilters) return;

        const urlParams = parseUrlParams();
        const noUrlFilters = !urlParams.teacher && (!urlParams.groups || urlParams.groups.length === 0) && !urlParams.course;

        if (!noUrlFilters) return;

        try {
            const raw = localStorage.getItem(lastPublicFilterKey);
            if (!raw) return;
            const saved = JSON.parse(raw);
            const savedTeacher = (saved?.teacher || '').trim();
            const savedGroups = Array.isArray(saved?.groups) ? saved.groups.filter(Boolean) : [];

            if (savedTeacher || savedGroups.length > 0) {
                // Update state and URL to reflect saved filter
                if (savedTeacher !== selectedTeacher) setSelectedTeacher(savedTeacher);
                if (JSON.stringify(savedGroups) !== JSON.stringify(selectedGroups)) setSelectedGroups(savedGroups);
                if (selectedCourse) setSelectedCourse('');
                setComplexFilterGroups(savedGroups.length > 0 ? [...savedGroups] : []);
                setComplexFilterTeachers(savedTeacher ? [savedTeacher] : []);
                setComplexFilterSubjects([]);
                setComplexFilterRooms([]);
                updateUrlWithFilters(savedTeacher, savedGroups, '');
            }
        } catch (_) {
            // ignore JSON parse errors
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parseUrlParams, updateUrlWithFilters, lastPublicFilterKey, shouldPersistPublicFilters]);

    // Save or clear last-used filter whenever teacher/groups change in public view
    useEffect(() => {
        if (!shouldPersistPublicFilters) return;

        const hasTeacher = !!(selectedTeacher && selectedTeacher.trim());
        const hasGroups = Array.isArray(selectedGroups) && selectedGroups.length > 0;

        if (hasTeacher || hasGroups) {
            const payload = {
                teacher: hasTeacher ? selectedTeacher.trim() : '',
                groups: hasGroups ? selectedGroups.filter(Boolean) : []
            };
            try {
                localStorage.setItem(lastPublicFilterKey, JSON.stringify(payload));
            } catch (_) { /* no-op */ }
        } else {
            try {
                localStorage.removeItem(lastPublicFilterKey);
            } catch (_) { /* no-op */ }
        }
    }, [selectedTeacher, selectedGroups, lastPublicFilterKey, shouldPersistPublicFilters]);

    // Single student group picker modal (ScheduleFilter is mounted twice for desktop/mobile; modal state lives here so it only renders once).
    const [isStudentGroupModalOpen, setStudentGroupModalOpen] = useState(false);
    const selectedGroupsModalDelayRef = useRef(selectedGroups);
    selectedGroupsModalDelayRef.current = selectedGroups;

    /** Same delay as former ScheduleFilter behavior: let cached URL / localStorage filters apply first. */
    const STUDENT_GROUP_MODAL_DELAY_MS = 1000;
    useEffect(() => {
        if (!(isPublicView && isStudentsBuild())) {
            return undefined;
        }
        const hasGroup =
            Array.isArray(selectedGroups) &&
            selectedGroups.length > 0 &&
            String(selectedGroups[0] || '').trim();
        if (hasGroup || isStudentGroupModalOpen) {
            return undefined;
        }
        const id = window.setTimeout(() => {
            const g = selectedGroupsModalDelayRef.current;
            const stillNoGroup =
                !Array.isArray(g) ||
                g.length === 0 ||
                !String(g[0] || '').trim();
            if (stillNoGroup) {
                setStudentGroupModalOpen(true);
            }
        }, STUDENT_GROUP_MODAL_DELAY_MS);
        return () => {
            window.clearTimeout(id);
        };
    }, [isPublicView, selectedGroups, isStudentGroupModalOpen]);

    const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
    const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
    const [viewMode, setViewMode] = useState('wide');
    const [fitViewHeight, setFitViewHeight] = useState(600); // Default height

    const timelineContext = useMemo(() => deriveScheduleTimelineContext({
        fallbackDate: currentDate
    }), [currentDate]);
    const { date: timelineDate, weekNumber: targetWeekNumber, year: targetYear } = timelineContext;

    // --- Memoized computed values to prevent recalculation ---
    const currentWeekLabel = useMemo(() => formatWeekLabel(getWeekStartDate(timelineDate)), [timelineDate]);

    // --- Derived values (computed from state) ---
    const isAdmin = !isPublicFacingBuild() && (user?.role === 'Admin' || isDemoLoggedIn());
    const canViewMeetings = useMemo(() => {
        if (isStudentsBuild()) return false;
        return isAdmin || user?.role === 'Teacher' || isTeachersBuild() || isAdminBuild();
    }, [isAdmin, user?.role]);
    const effectiveEntryType = canViewMeetings ? eventPanelEntryType : 'event';

    useEffect(() => {
        if (!canViewMeetings && eventPanelEntryType !== 'event') {
            setEventPanelEntryType('event');
        }
    }, [canViewMeetings, eventPanelEntryType]);

    // Memoize the expensive exception processing with early returns
    const entriesWithExceptions = useMemo(() => {
        if (!dataInitialized) {
            return [];
        }

        const safeBaseItems = Array.isArray(baseScheduleItems) ? baseScheduleItems : [];
        if (safeBaseItems.length === 0) {
            return [];
        }

        const validItems = safeBaseItems.filter(item => !!item);
        if (validItems.length === 0) {
            return [];
        }

        // Apply exceptions and get the correct entry state for the current week
        const result = validItems.map(entry => {
            if (entry && typeof entry.getEntryForWeek === 'function') {
                return entry.getEntryForWeek(targetWeekNumber, targetYear);
            }
            return entry;
        }).filter(Boolean); // Remove null entries (cancelled for the week)

        console.log(`[LiveScheduleView] After exception processing: ${result.length} entries remain`);
        return result;
    }, [baseScheduleItems, targetWeekNumber, targetYear, dataInitialized]);

    // Memoize the week-active filtering with optimizations
    const weekActiveEntries = useMemo(() => {
        if (!entriesWithExceptions || entriesWithExceptions.length === 0) {
            return [];
        }

        const filteredEntries = entriesWithExceptions.filter(entry => {
            if (!entry) return false;

            const targetWeekActive = entry.isActiveForWeekSync
                ? entry.isActiveForWeekSync(targetWeekNumber, targetYear)
                : (entry.isActiveForWeek
                    ? entry.isActiveForWeek(targetWeekNumber, targetYear)
                    : true);

            if (!targetWeekActive) {
                return false;
            }

            return true;
        });

        console.log(`[LiveScheduleView] Week filtering: ${entriesWithExceptions.length} -> ${filteredEntries.length} entries`);
        return filteredEntries;
    }, [entriesWithExceptions, targetWeekNumber, targetYear]);

    // Selected period for semester mode
    const selectedPeriod = useMemo(() => {
        if (!periods || periods.length === 0 || !selectedPeriodId) return null;
        return periods.find(p => String(p.id) === String(selectedPeriodId)) || null;
    }, [periods, selectedPeriodId]);

    // Entries visible in semester mode: any entry active in the selected period
    const semesterEntries = useMemo(() => {
        if (timeMode !== 'semester' || !selectedPeriod) return [];

        const source = Array.isArray(baseScheduleItems) ? baseScheduleItems : [];
        const minYear = selectedPeriod.year || currentDate.getFullYear();
        const maxYear = selectedPeriod.end_week < selectedPeriod.start_week ? (minYear + 1) : minYear;

        const result = source.filter(entry => {
            if (!entry) return false;
            // Unscheduled entries (no week series) are included
            if (typeof entry.isScheduled === 'function' && !entry.isScheduled()) return true;
            try {
                const weeks = typeof entry.getActiveWeeksSync === 'function' ? entry.getActiveWeeksSync(minYear, maxYear) : [];
                if (weeks && weeks.length > 0) {
                    return weeks.some(wk => typeof selectedPeriod.containsWeek === 'function' && selectedPeriod.containsWeek(wk.week, wk.year));
                }
            } catch (_) { /* no-op */ }
            return false;
        });

        console.log(`[LiveScheduleView] Semester filtering: ${source.length} -> ${result.length} entries`);
        return result;
    }, [timeMode, selectedPeriod, baseScheduleItems, currentDate]);

    // Base entries for subsequent user filters depending on mode
    const timeModeBaseEntries = useMemo(() => {
        const base = (timeMode === 'semester' && selectedPeriod) ? semesterEntries : weekActiveEntries;
        // Hide meetings in students build
        if (isStudentsBuild()) {
            return base.filter(entry => entry && entry.type !== 'meeting');
        }
        return base;
    }, [timeMode, selectedPeriod, semesterEntries, weekActiveEntries]);

    // Split filtering effect into smaller, focused effects
    // Effect 1: Handle filter view mode
    useEffect(() => {
        const teacherIsSelected = !!selectedTeacher;
        const safeSelectedGroups = Array.isArray(selectedGroups) ? selectedGroups : [];
        const groupsAreSelected = safeSelectedGroups.length > 0 && !isScheduleFilterAllGroupsOnly(safeSelectedGroups);

        const complexGroupFilterActive = complexFilterGroups.length > 0
            && !isScheduleFilterAllGroupsOnly(complexFilterGroups);

        // Check if complex filters are active
        const complexFiltersActive = complexGroupFilterActive ||
            complexFilterTeachers.length > 0 ||
            complexFilterSubjects.length > 0 ||
            complexFilterRooms.length > 0;

        const shouldShowFilteredView = teacherIsSelected
            || ((isPublicView || isStudentsBuild()) && groupsAreSelected)
            || complexFiltersActive;
        setIsFilteredView(shouldShowFilteredView);
    }, [selectedTeacher, selectedGroups, isPublicView, isStudentsBuild, complexFilterGroups, complexFilterTeachers, complexFilterSubjects, complexFilterRooms]);

    // Effect 2: Apply user filters (teacher, course, groups) - optimized
    const userFilteredEntries = useMemo(() => {
        if (!timeModeBaseEntries || timeModeBaseEntries.length === 0) {
            return [];
        }

        const teacherIsSelected = !!selectedTeacher;
        const safeSelectedGroups = Array.isArray(selectedGroups) ? selectedGroups : [];
        const groupsAreSelected = safeSelectedGroups.length > 0 && !isScheduleFilterAllGroupsOnly(safeSelectedGroups);

        const complexGroupFilterActive = complexFilterGroups.length > 0
            && !isScheduleFilterAllGroupsOnly(complexFilterGroups);

        // Check if complex filters are active
        const complexFiltersActive = complexGroupFilterActive ||
            complexFilterTeachers.length > 0 ||
            complexFilterSubjects.length > 0 ||
            complexFilterRooms.length > 0;

        // If no filters are applied, return all week-active entries
        if (!teacherIsSelected && !selectedCourse && !groupsAreSelected && !complexFiltersActive) {
            console.log(`[LiveScheduleView] No filters applied, returning ${timeModeBaseEntries.length} entries`);
            return timeModeBaseEntries;
        }

        const filteredEntries = timeModeBaseEntries.filter(entry => {
            if (!entry) return false;

            // Simple filters (legacy)
            const teacherMatch = !selectedTeacher || (
                typeof entry.hasTeacher === 'function'
                    ? entry.hasTeacher(selectedTeacher)
                    : (
                        entry.teacher === selectedTeacher || (
                            Array.isArray(entry.teachers) && entry.teachers.some(t => (t && t.name) === selectedTeacher)
                        )
                    )
            );
            const courseMatch = !selectedCourse || entry.course === selectedCourse;
            const groupMatch = !groupsAreSelected || entry.includesGroup(safeSelectedGroups);

            // Complex filters
            let complexGroupMatch = true;
            let complexTeacherMatch = true;
            let complexSubjectMatch = true;

            if (complexGroupFilterActive) {
                complexGroupMatch = entry.includesGroup(complexFilterGroups);
            }

            if (complexFilterTeachers.length > 0) {
                complexTeacherMatch = typeof entry.hasTeacher === 'function'
                    ? complexFilterTeachers.some(name => entry.hasTeacher(name))
                    : (() => {
                        const entryTeacherNames = [];
                        if (Array.isArray(entry.teachers)) {
                            entryTeacherNames.push(...entry.teachers.map(t => t && t.name).filter(Boolean));
                        }
                        if (entry.teacher) {
                            entryTeacherNames.push(entry.teacher);
                        }
                        return entryTeacherNames.some(name => complexFilterTeachers.includes(name));
                    })();
            }

            if (complexFilterSubjects.length > 0) {
                complexSubjectMatch = complexFilterSubjects.includes(entry.course);
            }

            // Complex room filter
            let complexRoomMatch = true;
            if (complexFilterRooms.length > 0) {
                const entryRoom = entry.room || '';
                complexRoomMatch = complexFilterRooms.includes(entryRoom);
            }

            // Apply all filters: simple filters AND complex filters
            return teacherMatch && courseMatch && groupMatch &&
                complexGroupMatch && complexTeacherMatch && complexSubjectMatch && complexRoomMatch;
        });

        console.log(`[LiveScheduleView] User filtering: ${timeModeBaseEntries.length} -> ${filteredEntries.length} entries`);
        return filteredEntries;
    }, [timeModeBaseEntries, selectedTeacher, selectedCourse, selectedGroups, complexFilterGroups, complexFilterTeachers, complexFilterSubjects, complexFilterRooms]);

    const filteredScheduleItems = userFilteredEntries;

    const uniqueRoomsForGrid = useMemo(() => getUniqueRooms(filteredScheduleItems), [filteredScheduleItems]);

    useEffect(() => {
        const snapshot = {
            hasInitialCachedSchedule,
            isLoading,
            dataInitialized,
            timeMode,
            selectedPeriodId,
            targetWeekNumber,
            targetYear,
            roomsCount: Array.isArray(rooms) ? rooms.length : 0,
            baseScheduleItems: summarizeScheduleEntriesForLog(baseScheduleItems),
            entriesWithExceptions: summarizeScheduleEntriesForLog(entriesWithExceptions),
            weekActiveEntries: summarizeScheduleEntriesForLog(weekActiveEntries),
            semesterEntries: summarizeScheduleEntriesForLog(semesterEntries),
            timeModeBaseEntries: summarizeScheduleEntriesForLog(timeModeBaseEntries),
            userFilteredEntries: summarizeScheduleEntriesForLog(userFilteredEntries),
            filteredScheduleItems: summarizeScheduleEntriesForLog(filteredScheduleItems),
        };
        const serialized = JSON.stringify(snapshot);
        if (schedulePipelineLogRef.current === serialized) {
            return;
        }
        schedulePipelineLogRef.current = serialized;
        console.groupCollapsed(
            `[LiveScheduleView][Pipeline] loading=${isLoading} initialized=${dataInitialized} mode=${timeMode} week=${targetWeekNumber}/${targetYear}`
        );
        console.log('[LiveScheduleView][Pipeline] snapshot', snapshot);
        console.groupEnd();
    }, [
        hasInitialCachedSchedule,
        isLoading,
        dataInitialized,
        timeMode,
        selectedPeriodId,
        targetWeekNumber,
        targetYear,
        rooms,
        baseScheduleItems,
        entriesWithExceptions,
        weekActiveEntries,
        semesterEntries,
        timeModeBaseEntries,
        userFilteredEntries,
        filteredScheduleItems,
    ]);

    // --- Refs ---
    const assetManager = useMemo(() => AssetManager.getInstance(), []);
    const periodManager = useMemo(() => PeriodManager.getInstance(), []);
    const academicYearService = useMemo(() => AcademicYearService.getInstance(), []);
    const fitViewContainerRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const dayHeaderRefs = useRef({});
    const wideDaySectionRefs = useRef({});
    /** After user changes week via WeekNavigator, do not auto-scroll to today's table. */
    const suppressScrollToTodayAfterWeekNavRef = useRef(false);
    const [currentVisibleDay, setCurrentVisibleDay] = useState(activeDays[0]);
    const [previousDay, setPreviousDay] = useState(activeDays[0]);

    // --- Holiday State ---
    const [weekHolidays, setWeekHolidays] = useState(new Map()); // Map<string, Holiday | null> - key is day name, value is Holiday object from API

    useEffect(() => {
        if (!scheduleSyncFeedback) {
            return undefined;
        }
        const t = window.setTimeout(() => setScheduleSyncFeedback(null), 4500);
        return () => clearTimeout(t);
    }, [scheduleSyncFeedback]);

    // Main effect to fetch and process schedule data - only run once on mount
    useEffect(() => {
        let isMounted = true;

        const fetchEntries = async () => {
            if (!isMounted) return;

            let showedStaleFromCache = false;
            try {
                setScheduleSyncFeedback(null);
                setIsLoading(true);

                if (hasForcedEntries) {
                    console.log('[LiveScheduleView] Using forced preview schedule entries:', forcedScheduleEntries.length);
                    setBaseScheduleItems(forcedScheduleEntries);
                    setScheduleSyncFeedback(null);
                    setDataInitialized(true);
                    setIsLoading(false);
                    return;
                }

                console.log('[LiveScheduleView] Fetching schedule entries using AssetManager.');
                const { changed: academicYearChanged, data: academicYearStructure } = await academicYearService.getAcademicYearStructureStaleAware();

                if (!isScheduleShare) {
                    const { rangeStart, rangeEnd } = getLiveScheduleDateRange();
                    const periodCandidates = await periodManager.fetchPeriods();
                    if (!isMounted) return;

                    const safePeriods = Array.isArray(periodCandidates) ? periodCandidates : [];
                    const periodsInRange = PeriodManager.getPeriodsOverlappingDateRange(safePeriods, rangeStart, rangeEnd);
                    const activePeriodsInRange = periodsInRange.filter(period => getPeriodActiveDraftId(period) !== null);

                    setPeriods(safePeriods);
                    if (safePeriods.length > 0) {
                        const matchingCurrentWeekPeriod = periodsInRange.find(p => (
                            typeof p.containsWeek === 'function' ? p.containsWeek(targetWeekNumber, targetYear) : false
                        ));
                        const fallbackPeriod = matchingCurrentWeekPeriod || periodsInRange[0] || safePeriods[0];
                        setSelectedPeriodId(currentSelectedPeriodId => {
                            if (!currentSelectedPeriodId || !safePeriods.some(p => String(p.id) === String(currentSelectedPeriodId))) {
                                return fallbackPeriod?.id ?? null;
                            }
                            return currentSelectedPeriodId;
                        });
                    }

                    const currentInclude = propInclude || '';
                    const fetchContexts = activePeriodsInRange
                        .filter(period => period?.id)
                        .map(period => ({
                            period,
                            filters: {
                                ...(propFilters || {}),
                                active_period_id: period.id,
                                active_schedule_draft_id: getPeriodActiveDraftId(period),
                            }
                        }));

                    console.log('[LiveScheduleView] Live period schedule fetch context:', {
                        rangeStart: rangeStart.toISOString(),
                        rangeEnd: rangeEnd.toISOString(),
                        totalPeriods: safePeriods.length,
                        periodsInRange: periodsInRange.map(period => ({
                            id: period.id,
                            name: period.name,
                            activeDraftId: getPeriodActiveDraftId(period)
                        })),
                        activePeriodsInRange: activePeriodsInRange.length,
                        currentInclude,
                        academicYearChanged,
                        academicYear: academicYearStructure?.academicYear || null,
                    });

                    if (fetchContexts.length === 0) {
                        console.log('[LiveScheduleView] No periods with active schedule drafts in live display range.');
                        setScheduleSyncFeedback(null);
                        setBaseScheduleItems([]);
                        setDataInitialized(true);
                        return;
                    }

                    const cachedGroups = fetchContexts.map(({ filters }) => assetManager.getCachedScheduleEntries(filters, currentInclude));
                    const hasCompleteCachedLiveRange = cachedGroups.every(entries => entries !== null);
                    if (hasCompleteCachedLiveRange) {
                        showedStaleFromCache = true;
                        const cachedEntries = mergeScheduleEntries(cachedGroups);
                        console.log('[LiveScheduleView] Showing cached active-period schedules while refreshing:', cachedEntries.length, 'entries');
                        if (!isMounted) return;
                        setError(null);
                        setBaseScheduleItems(cachedEntries);
                        setDataInitialized(true);
                    }

                    const periodResults = await Promise.all(fetchContexts.map(async ({ period, filters }) => {
                        const result = await assetManager.fetchScheduleEntriesStaleAware(
                            filters,
                            currentInclude,
                            scheduleEntriesFetchOptions
                        );
                        return { ...result, period };
                    }));

                    if (!isMounted) return;

                    const scheduleEntries = mergeScheduleEntries(periodResults.map(result => result.data || []));
                    const changed = periodResults.some(result => result.changed);
                    const liveScheduleVersion = periodResults.find(result => result.liveScheduleVersion != null)?.liveScheduleVersion ?? null;
                    const scheduleEntriesRequestSkipped = periodResults.every(result => result.scheduleEntriesRequestSkipped);

                    if (liveScheduleVersion != null) {
                        const upToDate = scheduleEntriesRequestSkipped || !changed;
                        setScheduleSyncFeedback(upToDate
                            ? { kind: 'upToDate', version: liveScheduleVersion }
                            : { kind: 'updated', version: liveScheduleVersion }
                        );
                    } else {
                        setScheduleSyncFeedback(null);
                    }

                    if (showedStaleFromCache && !changed && !academicYearChanged && scheduleEntriesRequestSkipped) {
                        console.log('[LiveScheduleView] Fresh active-period schedules match cache, keeping current displayed state.');
                        setDataInitialized(true);
                        return;
                    }

                    console.log('[LiveScheduleView] Applying active-period schedules to state:', summarizeScheduleEntriesForLog(scheduleEntries));
                    setBaseScheduleItems(scheduleEntries);
                    setDataInitialized(true);
                    return;
                }

                const currentFilters = { ...(propFilters || {}) };
                const currentInclude = propInclude || '';
                console.log('[LiveScheduleView] Fetch start context:', {
                    currentFilters,
                    currentInclude,
                    hasInitialCachedSchedule,
                    initialCachedScheduleCount: initialCachedScheduleItems.length,
                    initialStateBaseCount: Array.isArray(baseScheduleItems) ? baseScheduleItems.length : 0,
                    initialStateFilteredCount: Array.isArray(filteredScheduleItems) ? filteredScheduleItems.length : 0,
                    academicYearChanged,
                    academicYear: academicYearStructure?.academicYear || null,
                    academicStartWeek: academicYearStructure?.startWeek ?? null,
                    academicEndWeek: academicYearStructure?.endWeek ?? null,
                    targetWeekNumber,
                    targetYear,
                    timeMode,
                    activePeriodScheduleId: null,
                });

                const cachedEntries = assetManager.getCachedScheduleEntries(currentFilters, currentInclude);
                if (cachedEntries !== null) {
                    showedStaleFromCache = true;
                    console.log('[LiveScheduleView] Showing cached schedule while refreshing:', cachedEntries.length, 'entries');
                    console.log('[LiveScheduleView] Cached schedule sample:', summarizeScheduleEntriesForLog(cachedEntries));
                    if (!isMounted) return;
                    setError(null);
                    console.log('[LiveScheduleView] Applying cached entries to state:', summarizeScheduleEntriesForLog(cachedEntries));
                    setBaseScheduleItems(cachedEntries);
                    setDataInitialized(true);
                }

                // Potentially, AssetManager could accept year/week to fetch only relevant data
                // For now, it fetches all, and we filter client-side
                const {
                    data: scheduleEntries,
                    changed,
                    liveScheduleVersion,
                    scheduleEntriesRequestSkipped,
                } = await assetManager.fetchScheduleEntriesStaleAware(
                    currentFilters,
                    currentInclude,
                    scheduleEntriesFetchOptions
                );
                console.log('[LiveScheduleView] Received ScheduleEntry objects from AssetManager:', scheduleEntries?.length || 0, 'changed:', changed, 'liveScheduleVersion:', liveScheduleVersion, 'skippedScheduleHttp:', scheduleEntriesRequestSkipped);
                console.log('[LiveScheduleView] Fresh schedule sample from AssetManager:', summarizeScheduleEntriesForLog(scheduleEntries));

                if (!isMounted) return;

                if (!isScheduleShare && liveScheduleVersion != null) {
                    const upToDate = scheduleEntriesRequestSkipped || !changed;
                    const feedback = upToDate
                        ? { kind: 'upToDate', version: liveScheduleVersion }
                        : { kind: 'updated', version: liveScheduleVersion };
                    console.info('[ScheduleSync][LiveScheduleView] showing feedback banner', {
                        ...feedback,
                        scheduleEntriesRequestSkipped,
                        changed,
                        academicYearChanged,
                    });
                    setScheduleSyncFeedback(feedback);
                } else {
                    console.info('[ScheduleSync][LiveScheduleView] no version banner', {
                        isScheduleShare,
                        liveScheduleVersion,
                        reason: isScheduleShare ? 'schedule share view' : liveScheduleVersion == null ? 'no version from API' : 'unknown',
                    });
                    setScheduleSyncFeedback(null);
                }

                if (showedStaleFromCache && !changed && !academicYearChanged) {
                    console.log('[LiveScheduleView] Fresh schedule matches cache, keeping current displayed state.');
                    setDataInitialized(true);
                    return;
                }

                if (scheduleEntries) {
                    console.log('[LiveScheduleView] Applying fresh entries to state:', summarizeScheduleEntriesForLog(scheduleEntries));
                    setBaseScheduleItems(scheduleEntries);
                } else {
                    console.log('[LiveScheduleView] No schedule entries available after fetch; clearing schedule state.');
                    setBaseScheduleItems([]);
                }
                setDataInitialized(true);
            } catch (err) {
                if (!isMounted) return;
                console.error('[LiveScheduleView] Error fetching schedule entries via AssetManager:', err);
                setScheduleSyncFeedback(null);
                setError(err.message || 'Failed to load schedule entries.');
                if (!showedStaleFromCache) {
                    console.log('[LiveScheduleView] Fetch failed without cache; clearing schedule state.');
                    setBaseScheduleItems([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchEntries();

        return () => {
            isMounted = false;
        };
    }, [assetManager, periodManager, propFilters, propInclude, scheduleEntriesFetchOptions, isScheduleShare, hasForcedEntries, forcedScheduleEntries]);

    // Load periods when switching to Semester or Occupancy mode
    useEffect(() => {
        let isMounted = true;
        const loadPeriods = async () => {
            if (timeMode !== 'semester' && timeMode !== 'occupancy') return;
            try {
                const fetched = await assetManager.fetchPeriods();
                if (!isMounted) return;
                const safe = Array.isArray(fetched) ? fetched : [];
                setPeriods(safe);
                if (safe.length > 0) {
                    const wk = getWeekNumber(currentDate);
                    const yr = currentDate.getFullYear();
                    const match = safe.find(p => typeof p.containsWeek === 'function' ? p.containsWeek(wk, yr) : false) || safe[0];
                    if (!selectedPeriodId || !safe.some(p => String(p.id) === String(selectedPeriodId))) {
                        setSelectedPeriodId(match?.id ?? safe[0].id);
                    }
                }
            } catch (e) {
                console.warn('[LiveScheduleView] Failed to fetch periods:', e);
                setPeriods([]);
            }
        };
        loadPeriods();
        return () => { isMounted = false; };
    }, [timeMode, assetManager, currentDate, selectedPeriodId]);
    // Debounced effect to load holiday information for the current week
    useEffect(() => {
        let isMounted = true;
        let timeoutId;

        const loadHolidays = async () => {
            try {
                console.log(`[LiveScheduleView] Loading holidays for week ${targetWeekNumber}, year ${targetYear}`);

                const holidayMap = new Map();

                // Check each day of the week for holidays
                for (const day of activeDays) {
                    try {
                        const holiday = await academicYearService.getHolidayForDay(day, targetWeekNumber, targetYear);
                        holidayMap.set(day, holiday);

                        if (holiday) {
                            console.log(`[LiveScheduleView] Found holiday on ${day}: ${holiday.name} (${holiday.type})`);
                        }
                    } catch (error) {
                        console.warn(`[LiveScheduleView] Error checking holiday for ${day}:`, error);
                        holidayMap.set(day, null);
                    }
                }

                if (isMounted) {
                    setWeekHolidays(holidayMap);
                }
            } catch (error) {
                console.error('[LiveScheduleView] Error loading holidays:', error);
                if (isMounted) {
                    setWeekHolidays(new Map());
                }
            }
        };

        // Debounce holiday loading to prevent excessive API calls
        timeoutId = setTimeout(loadHolidays, 150);

        return () => {
            isMounted = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [targetWeekNumber, targetYear, academicYearService]);

    // Function to determine animation direction
    const getAnimationDirection = useCallback(() => {
        const currentIndex = activeDays.indexOf(currentVisibleDay);
        const previousIndex = activeDays.indexOf(previousDay);
        return currentIndex > previousIndex ? 'slideUp' : 'slideDown';
    }, [currentVisibleDay, previousDay, activeDays]);

    // Add throttled scroll event listener - optimized version
    useEffect(() => {
        // Only set up scroll listener after loading is complete
        if (isLoading) return;

        let ticking = false;

        // Create a throttled scroll handler to improve performance
        const scrollHandler = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    if (!scrollContainerRef.current) {
                        ticking = false;
                        return;
                    }

                    const scrollTop = scrollContainerRef.current.scrollTop;
                    let foundDay = activeDays[0]; // Default to the first day

                    // Iterate through day headers to find the one currently at the top
                    for (const day of activeDays) {
                        const headerElement = dayHeaderRefs.current[day];
                        if (headerElement) {
                            const offsetTop = headerElement.offsetTop;
                            const headerHeight = headerElement.offsetHeight;
                            const activationOffset = headerHeight * 4;

                            if (scrollTop >= offsetTop - activationOffset) {
                                foundDay = day;
                            } else {
                                break;
                            }
                        }
                    }

                    // Use functional updates to avoid stale closures
                    setCurrentVisibleDay(currentDay => {
                        if (foundDay !== currentDay) {
                            setPreviousDay(currentDay);
                            return foundDay;
                        }
                        return currentDay;
                    });

                    ticking = false;
                });
                ticking = true;
            }
        };

        // Add listener with a delay to ensure DOM is ready
        const timer = setTimeout(() => {
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', scrollHandler);
                scrollHandler(); // Initial call to set correct day
            } else {
                console.warn('[LiveScheduleView] scrollContainer ref is still null after loading completed');
            }
        }, 100); // Increased delay to ensure DOM is fully ready

        return () => {
            clearTimeout(timer);
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', scrollHandler);
            }
        };
    }, [isLoading]); // Depend on isLoading to set up listener after loading completes

    // --- Effects ---
    // useEffect(() => {
    //     console.log("User Role:", user?.role);
    //     setFilteredScheduleItems(Array.isArray(baseScheduleItems) ? baseScheduleItems : []);
    // }, [user, baseScheduleItems]);

    const toggleViewMode = () => {
        setViewMode(prevMode => (prevMode === 'wide' ? 'fit' : 'wide'));
    };

    useEffect(() => {
        if (viewMode !== 'fit') return;
        const element = fitViewContainerRef.current;
        if (!element) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const newWidth = entry.contentRect.width;
                if (newWidth > 0) {
                    setFitViewHeight(newWidth * A4_ASPECT_RATIO);
                }
            }
        });
        resizeObserver.observe(element);
        const initialWidth = element.getBoundingClientRect().width;
        if (initialWidth > 0) {
            setFitViewHeight(initialWidth * A4_ASPECT_RATIO);
        }
        return () => resizeObserver.disconnect();
    }, [viewMode]);

    // --- Modal Handlers (read-only detail view) ---
    const openScheduleModal = useCallback((item) => {
        setSelectedScheduleItem(item);
        setIsDetailViewOpen(true);
    }, []);

    const closeDetailView = useCallback(() => {
        setIsDetailViewOpen(false);
        setSelectedScheduleItem(null);
    }, []);

    // --- Filter Change Handlers ---
    const handleTeacherChange = useCallback((value) => {
        setSelectedTeacher(value);
        if (value) {
            setSelectedCourse('');
            setSelectedGroups([]);
        }
        setComplexFilterTeachers(value ? [value] : []);
        setComplexFilterSubjects([]);
        setComplexFilterGroups([]);
        updateUrlWithFilters(value, [], '');
    }, [updateUrlWithFilters]);
    const handleCourseChange = useCallback((value) => {
        setSelectedCourse(value);
        if (value) {
            setSelectedTeacher('');
            setSelectedGroups([]);
        }
        setComplexFilterSubjects(value ? [value] : []);
        setComplexFilterTeachers([]);
        setComplexFilterGroups([]);
        updateUrlWithFilters('', [], value);
    }, [updateUrlWithFilters]);
    const handleGroupChange = useCallback((selectedGroupsValue) => {
        let groupsToSet = [];

        const singleGroupMode = isPublicView || isStudentsBuild();
        if (singleGroupMode) {
            if (selectedGroupsValue == null || selectedGroupsValue === '') {
                groupsToSet = isStudentsBuild() ? [SCHEDULE_FILTER_ALL_GROUPS] : [];
            } else if (Array.isArray(selectedGroupsValue)) {
                groupsToSet = selectedGroupsValue.length > 0 ? [selectedGroupsValue[0]] : [];
            } else {
                groupsToSet = [selectedGroupsValue];
            }
            if (!isStudentsBuild() && groupsToSet.length === 1 && groupsToSet[0] === SCHEDULE_FILTER_ALL_GROUPS) {
                groupsToSet = [];
            }
        } else {
            groupsToSet = Array.isArray(selectedGroupsValue) ? selectedGroupsValue : [];
        }
        setSelectedGroups(groupsToSet);
        if (groupsToSet.length > 0) {
            setSelectedTeacher('');
            setSelectedCourse('');
        }

        setComplexFilterGroups(groupsToSet.length > 0 ? [...groupsToSet] : []);
        if (groupsToSet.length > 0) {
            setComplexFilterTeachers([]);
            setComplexFilterSubjects([]);
        }

        updateUrlWithFilters('', groupsToSet, '');
    }, [isPublicView, isStudentsBuild, updateUrlWithFilters]);
    const clearFilters = useCallback(() => {
        setSelectedTeacher('');
        setSelectedCourse('');
        setStudentGroupModalOpen(false);
        setComplexFilterGroups([]);
        setComplexFilterTeachers([]);
        setComplexFilterSubjects([]);
        setComplexFilterRooms([]);

        const groupsAfterClear = isStudentsBuild() ? [SCHEDULE_FILTER_ALL_GROUPS] : [];
        setSelectedGroups(groupsAfterClear);

        updateUrlWithFilters('', groupsAfterClear, '');
    }, [updateUrlWithFilters, isStudentsBuild]);

    const handleOpenComplexFilter = useCallback(() => {
        setIsComplexFilterModalOpen(true);
    }, []);

    const handleCloseComplexFilter = useCallback(() => {
        setIsComplexFilterModalOpen(false);
    }, []);

    const handleApplyComplexFilters = useCallback((filters) => {
        setComplexFilterGroups(filters.groups || []);
        setComplexFilterTeachers(filters.teachers || []);
        setComplexFilterSubjects(filters.subjects || []);
        setComplexFilterRooms(filters.rooms || []);

        const primaryTeacher = filters.teachers && filters.teachers.length > 0 ? filters.teachers[0] : '';
        const primarySubject = filters.subjects && filters.subjects.length > 0 ? filters.subjects[0] : '';
        const combinedGroups = [...(filters.groups || [])];

        setSelectedTeacher(primaryTeacher);
        setSelectedCourse(primarySubject);
        setSelectedGroups(combinedGroups);

        updateUrlWithFilters(primaryTeacher, combinedGroups, primarySubject);
    }, [updateUrlWithFilters]);

    // --- Memoized Lists for Filters ---
    const uniqueTeachersForFilter = useMemo(() => {
        const names = (teachers || [])
            .map(t => (t?.name ?? '').toString().trim())
            .filter(n => n && n.toUpperCase() !== '__PLACEHOLDER__');
        const deduped = Array.from(new Set(names));
        deduped.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        return [''].concat(deduped);
    }, [teachers]);

    const uniqueCoursesForFilter = useMemo(() => {
        const courses = new Set(baseScheduleItems.map(entry => entry?.course).filter(Boolean));
        return [''].concat(Array.from(courses).sort());
    }, [baseScheduleItems]);
    const uniqueGroupsForFilter = useMemo(() => {
        const groupNames = (groups || []).map(group => group.name || group.group_code || group).filter(Boolean);
        return [''].concat(groupNames.sort());
    }, [groups]);

    // Rooms filtered for column view when multiple rooms are selected
    const filteredRoomsForColumns = useMemo(() => {
        if (!Array.isArray(rooms)) return [];
        if (complexFilterRooms.length > 1) {
            const selected = new Set((complexFilterRooms || []).map(v => String(v)));
            return rooms.filter(r => {
                const candidates = [
                    r && r.name,
                    r && (r.roomCode || r.room_code),
                    r && (r.id != null ? String(r.id) : null)
                ].filter(Boolean);
                return candidates.some(val => selected.has(String(val)));
            });
        }
        return rooms || [];
    }, [rooms, complexFilterRooms]);

    // Decide when to use the linear filtered view (always use when filtered, regardless of rooms selection)
    const useLinearFilteredView = useMemo(() => {
        return !!isFilteredView;
    }, [isFilteredView]);

    const hasRooms = Array.isArray(rooms) && rooms.length > 0;
    const roomsCachePeek = assetManager.peekCachedRoomsRaw();
    const roomsDataReady = hasRooms || roomsCachePeek !== null || !isAssetsLoading;
    const isWaitingForRooms = !roomsDataReady;
    const showScheduleLoading = isLoading || isWaitingForRooms;
    const showCompactLoadingIndicator = showScheduleLoading && hasInitialCachedSchedule && !isWaitingForRooms;

    useEffect(() => {
        // Scroll as soon as schedule data is ready (cached entries: dataInitialized is true on mount while isLoading may still run).
        if (!dataInitialized || daysLoading) return;
        if (viewMode !== 'wide') return;
        if (timeMode === 'occupancy' || timeMode === 'events' || useLinearFilteredView) return;
        if (!todayWideDayKey) return;
        if (suppressScrollToTodayAfterWeekNavRef.current) return;

        const scrollOvershootPx = 100;

        let rafKick = 0;
        let rafSmooth = 0;
        rafKick = requestAnimationFrame(() => {
            const container = scrollContainerRef.current;
            const el = wideDaySectionRefs.current[todayWideDayKey];
            if (!el || !container) return;

            const elRect = el.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const targetScrollTop = container.scrollTop + (elRect.top - containerRect.top);

            const intermediate = Math.max(0, targetScrollTop - scrollOvershootPx);
            container.scrollTop = intermediate;

            rafSmooth = requestAnimationFrame(() => {
                container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
            });
        });

        return () => {
            cancelAnimationFrame(rafKick);
            cancelAnimationFrame(rafSmooth);
        };
    }, [dataInitialized, daysLoading, viewMode, timeMode, useLinearFilteredView, todayWideDayKey]);

    // Rooms list for fit view (strings); use selected rooms when >1 selected
    const roomsToUseFit = useMemo(() => {
        if (complexFilterRooms.length > 1) {
            return (filteredRoomsForColumns || [])
                .map(r => r?.name || r?.roomCode || r?.room_code || (r?.id != null ? String(r.id) : ''))
                .filter(Boolean);
        }
        return uniqueRoomsForGrid;
    }, [complexFilterRooms, filteredRoomsForColumns, uniqueRoomsForGrid]);

    // --- Render Helpers ---

    const handleCalendarIconClick = useCallback(() => {
        // This can be used for additional logic if needed
        console.log('[LiveScheduleView] Calendar icon clicked');
    }, []);

    const handleWeekSelect = useCallback((selectedDate) => {
        suppressScrollToTodayAfterWeekNavRef.current = true;
        console.log('[LiveScheduleView] Week selected:', selectedDate);
        setCurrentDate(selectedDate);
    }, []);

    // Period navigation helpers for Semester mode
    const currentPeriodIndex = useMemo(() => {
        if (!periods || periods.length === 0 || !selectedPeriodId) return -1;
        return periods.findIndex(p => String(p.id) === String(selectedPeriodId));
    }, [periods, selectedPeriodId]);

    const handlePrevPeriod = useCallback(() => {
        if (!periods || periods.length === 0) return;
        if (currentPeriodIndex > 0) {
            setSelectedPeriodId(periods[currentPeriodIndex - 1].id);
        }
    }, [periods, currentPeriodIndex]);

    const handleNextPeriod = useCallback(() => {
        if (!periods || periods.length === 0) return;
        if (currentPeriodIndex >= 0 && currentPeriodIndex < periods.length - 1) {
            setSelectedPeriodId(periods[currentPeriodIndex + 1].id);
        }
    }, [periods, currentPeriodIndex]);

    const currentPeriodLabel = useMemo(() => {
        if (!selectedPeriod) return 'No semester';
        if (typeof selectedPeriod.getDisplayString === 'function') return selectedPeriod.getDisplayString();
        return `${selectedPeriod.name} (Week ${selectedPeriod.start_week}-${selectedPeriod.end_week})`;
    }, [selectedPeriod]);

    const handlePrevWeek = useCallback(() => {
        suppressScrollToTodayAfterWeekNavRef.current = true;
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setDate(prevDate.getDate() - 7);
            console.log("[LiveScheduleView] Navigating to Previous Week, New Date:", newDate);
            return newDate;
        });
    }, []);

    const handleNextWeek = useCallback(() => {
        suppressScrollToTodayAfterWeekNavRef.current = true;
        setCurrentDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setDate(prevDate.getDate() + 7);
            console.log("[LiveScheduleView] Navigating to Next Week, New Date:", newDate);
            return newDate;
        });
    }, []);

    // Handle calendar sub-mode changes (weeks/semester), track last non-occupancy
    const handleCalendarModeChange = useCallback((mode) => {
        if (mode !== 'occupancy') {
            setLastCalendarMode(mode);
        }
        setTimeMode(mode);
    }, []);

    // Left drawer handlers
    const closeLeftDrawer = useCallback(() => {
        setIsLeftDrawerAnimating(false);
        // Wait for transition to finish before unmounting
        setTimeout(() => setIsLeftDrawerOpen(false), 300);
    }, []);
    const openLeftDrawer = useCallback(() => {
        setIsLeftDrawerOpen(true);
        // Start animation on next frame to ensure initial off-screen state is applied
        requestAnimationFrame(() => setIsLeftDrawerAnimating(true));
    }, []);
    const handleSelectPrimaryView = useCallback((view) => {
        if (view === 'calendar') {
            setTimeMode(prev => (lastCalendarMode || 'weeks'));
        } else if (view === 'events') {
            setTimeMode('events');
        } else if (view === 'occupancy') {
            setTimeMode('occupancy');
        }
        closeLeftDrawer();
    }, [lastCalendarMode, closeLeftDrawer]);

    const handleChangeInstitution = useCallback(() => {
        clearLastInstitutionUrlEndpoint();
        navigate('/', { replace: true });
    }, [navigate]);

    const shouldRedirectToAdminLogin = isAdminBuild()
        && typeof error === 'string'
        && error.includes('Access Token is required');

    useEffect(() => {
        if (!shouldRedirectToAdminLogin) return;
        navigate('/pi-admin', { replace: true });
    }, [navigate, shouldRedirectToAdminLogin]);

    // --- Error State ---
    if (error) {
        if (shouldRedirectToAdminLogin) {
            return null;
        }
        return (
            <div className="flex items-center justify-center h-screen bg-white">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error}</p>
                    <Button variant="primary" onClick={() => window.location.reload()}>Retry</Button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="relative flex flex-col h-screen pt-4 px-4 md:pt-6 md:px-6 lg:pt-4 lg:px-4 bg-gray-50 overflow-hidden"
            aria-busy={showScheduleLoading}
        >
            {showScheduleLoading && (
                showCompactLoadingIndicator ? (
                    <div
                        className="pointer-events-none absolute top-2 right-3 z-[60] flex items-center gap-2 rounded-md bg-white/90 px-2.5 py-1 text-xs text-gray-500 shadow-sm ring-1 ring-gray-200/80 backdrop-blur-sm md:top-3 md:right-4"
                        role="status"
                        aria-live="polite"
                    >
                        <span
                            className="inline-block size-3.5 shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500"
                            aria-hidden
                        />
                        <span>Loading schedule…</span>
                    </div>
                ) : (
                    <div className="pointer-events-none absolute inset-0 z-[60] flex items-center justify-center">
                        <div
                            className="flex items-center gap-3 rounded-lg bg-white/95 px-4 py-3 text-sm text-gray-600 shadow-md ring-1 ring-gray-200/80 backdrop-blur-sm"
                            role="status"
                            aria-live="polite"
                        >
                            <span
                                className="inline-block size-5 shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500"
                                aria-hidden
                            />
                            <span>Loading schedule…</span>
                        </div>
                    </div>
                )
            )}
            {!isLoading && scheduleSyncFeedback && (
                <div
                    className={`pointer-events-none absolute top-2 right-3 z-[200] max-w-[min(100vw-1.5rem,20rem)] rounded-md px-2.5 py-1 text-xs shadow-sm ring-1 backdrop-blur-sm md:top-3 md:right-4 ${scheduleSyncFeedback.kind === 'upToDate'
                        ? 'bg-emerald-50/95 text-emerald-800 ring-emerald-200/90'
                        : 'bg-sky-50/95 text-sky-900 ring-sky-200/90'
                        }`}
                    role="status"
                    aria-live="polite"
                >
                    {scheduleSyncFeedback.kind === 'upToDate'
                        ? `Up to date (${scheduleSyncFeedback.version})`
                        : `Updated to last version (${scheduleSyncFeedback.version})`}
                </div>
            )}
            {showScheduleShareBanner && (
                <div className="absolute top-0 left-0 z-50 overflow-hidden" style={{ width: 180, height: 180 }}>
                    <div
                        className="absolute bg-purple-600 text-white text-base font-extrabold tracking-[0.2em] uppercase text-center shadow-md py-1.5 px-3 flex items-center justify-center gap-2"
                        style={{ top: '46px', left: '-74px', width: '280px', transform: 'rotate(-45deg)' }}
                    >
                        <span>Shared</span>
                        {isAdmin && scheduleShareSlug && (
                            <button
                                type="button"
                                onClick={() => navigate(
                                    `/setup/class-scheduler?manageShare=${encodeURIComponent(scheduleShareSlug)}`
                                )}
                                className="rounded-full p-0.5 text-white/95 hover:bg-purple-700/70 hover:text-white"
                                title="Manage this shared link"
                                aria-label="Manage this shared link"
                            >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5h6m0 0v6m0-6L10 14M19 13v5a1 1 0 01-1 1H6a1 1 0 01-1-1V6a1 1 0 011-1h5" />
                                </svg>
                            </button>
                        )}
                        <HelpIcon
                            documentationId="shared-schedule-view"
                            variant="help"
                            size={16}
                            position="relative"
                            className="text-white/95 hover:text-white hover:bg-purple-700/70 rounded-full"
                        />
                    </div>
                </div>
            )}
            <StudentGroupSelectionModal
                isOpen={isStudentGroupModalOpen}
                onClose={() => setStudentGroupModalOpen(false)}
                groups={uniqueGroupsForFilter}
                selectedGroups={selectedGroups}
                onGroupSelect={handleGroupChange}
            />
            {/* Sticky Header */}
            <div className="flex-shrink-0 bg-gray-50 z-40 shadow-sm pb-2">
                {/* Desktop Layout: School Header, Filters, and Time Mode/Week Nav in same row */}
                <div className={`${isMobileLayout ? 'hidden' : 'flex'} items-center justify-between gap-4 mb-2`}>
                    {/* Left: Drawer toggle + School Header */}
                    <div className="flex items-center flex-shrink-0">
                        <Button
                            variant="ghost"
                            onClick={openLeftDrawer}
                            className="p-2 rounded-md text-gray-700 hover:text-blue-600"
                            title="Open view menu"
                            aria-label="Open view menu"
                        >
                            {/* Hamburger icon */}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 5h14a1 1 0 110 2H3a1 1 0 110-2zm0 4h14a1 1 0 110 2H3a1 1 0 110-2zm0 4h14a1 1 0 110 2H3a1 1 0 110-2z" clipRule="evenodd" />
                            </svg>
                        </Button>
                        <SchoolHeader />
                    </div>
                    {/* Filters in the middle (hidden in occupancy view) */}
                    {timeMode !== 'occupancy' && (
                        <div className={isPublicView ? "flex-shrink-0 max-w-[500px]" : "flex-1"}>
                            <ScheduleFilter
                                teachers={uniqueTeachersForFilter} courses={uniqueCoursesForFilter} groups={uniqueGroupsForFilter}
                                selectedTeacher={selectedTeacher} selectedCourse={selectedCourse} selectedGroups={selectedGroups}
                                onTeacherChange={handleTeacherChange} onCourseChange={handleCourseChange} onGroupChange={handleGroupChange}
                                onClearFilters={clearFilters}
                                isPublicView={isPublicView}
                                onOpenComplexFilter={handleOpenComplexFilter}
                                complexFilterGroups={complexFilterGroups}
                                complexFilterTeachers={complexFilterTeachers}
                                complexFilterSubjects={complexFilterSubjects}
                                complexFilterRooms={complexFilterRooms}
                                showTeacher={!isStudentsBuild()}
                                showCourse={false}
                                showGroups={!isTeachersBuild()}
                                onOpenStudentGroupModal={() => setStudentGroupModalOpen(true)}
                            />
                        </div>
                    )}
                    {/* Unified WeekNavigator with time mode + period nav inside (hidden in events-only view) */}
                    {timeMode !== 'events' && (
                        <div className="flex-shrink-0">
                            <WeekNavigator
                                currentWeekLabel={currentWeekLabel}
                                onCalendarIconClick={handleCalendarIconClick}
                                onPrevWeek={handlePrevWeek}
                                onNextWeek={handleNextWeek}
                                currentDate={currentDate}
                                onWeekSelect={handleWeekSelect}
                                timeMode={timeMode}
                                onTimeModeChange={handleCalendarModeChange}
                                currentPeriodLabel={currentPeriodLabel}
                                currentPeriodIndex={currentPeriodIndex}
                                totalPeriods={periods?.length || 0}
                                onPrevPeriod={handlePrevPeriod}
                                onNextPeriod={handleNextPeriod}
                            />
                        </div>
                    )}
                </div>

                {/* Mobile Layout: Stacked layout */}
                <div className={`${isMobileLayout ? 'block' : 'hidden'} mb-2`}>
                    {/* School Header with Drawer toggle and mobile filter on the right */}
                    <div className="flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                onClick={openLeftDrawer}
                                className="p-2 rounded-md text-gray-700 hover:text-blue-600"
                                title="Open view menu"
                                aria-label="Open view menu"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 5h14a1 1 0 110 2H3a1 1 0 110-2zm0 4h14a1 1 0 110 2H3a1 1 0 110-2zm0 4h14a1 1 0 110 2H3a1 1 0 110-2z" clipRule="evenodd" />
                                </svg>
                            </Button>
                            <SchoolHeader />
                        </div>
                        {timeMode !== 'occupancy' && (
                            <div className="ml-2">
                                <ScheduleFilter
                                    teachers={uniqueTeachersForFilter}
                                    courses={uniqueCoursesForFilter}
                                    groups={uniqueGroupsForFilter}
                                    selectedTeacher={selectedTeacher}
                                    selectedCourse={selectedCourse}
                                    selectedGroups={selectedGroups}
                                    onTeacherChange={handleTeacherChange}
                                    onCourseChange={handleCourseChange}
                                    onGroupChange={handleGroupChange}
                                    onClearFilters={clearFilters}
                                    isPublicView={isPublicView}
                                    onOpenComplexFilter={handleOpenComplexFilter}
                                    complexFilterGroups={complexFilterGroups}
                                    complexFilterTeachers={complexFilterTeachers}
                                    complexFilterSubjects={complexFilterSubjects}
                                    complexFilterRooms={complexFilterRooms}
                                    showTeacher={!isStudentsBuild()}
                                    showCourse={false}
                                    showGroups={!isTeachersBuild()}
                                    compactMobile={true}
                                    onOpenStudentGroupModal={() => setStudentGroupModalOpen(true)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Unified WeekNavigator (mobile); hidden in events-only view */}
                    {timeMode !== 'events' && (
                        <div className={`flex justify-center ${isMobileLayout ? 'mt-2' : 'mt-3'}`}>
                            <WeekNavigator
                                currentWeekLabel={currentWeekLabel}
                                onCalendarIconClick={handleCalendarIconClick}
                                onPrevWeek={handlePrevWeek}
                                onNextWeek={handleNextWeek}
                                currentDate={currentDate}
                                onWeekSelect={handleWeekSelect}
                                timeMode={timeMode}
                                onTimeModeChange={handleCalendarModeChange}
                                currentPeriodLabel={currentPeriodLabel}
                                currentPeriodIndex={currentPeriodIndex}
                                totalPeriods={periods?.length || 0}
                                onPrevPeriod={handlePrevPeriod}
                                onNextPeriod={handleNextPeriod}
                            />
                        </div>
                    )}
                </div>

                {/* Fixed Day Header */}
                <FixedDayHeader
                    currentVisibleDay={currentVisibleDay}
                    currentDate={currentDate}
                    animationDirection={getAnimationDirection()}
                    show={viewMode === 'wide' && timeMode !== 'occupancy' && timeMode !== 'events' && !useLinearFilteredView && !showScheduleLoading}
                />
            </div>

            {/* Schedule scrolls here; events/meetings panel beside schedule on desktop only (hidden on mobile). */}
            <div className="flex flex-col flex-grow min-h-0 overflow-hidden">
                <div className={`flex flex-col lg:flex-row flex-1 min-h-0 gap-4 overflow-hidden ${timeMode === 'occupancy' || timeMode === 'events' ? 'max-h-full' : ''}`}>
                    <div
                        ref={scrollContainerRef}
                        className={`flex flex-1 flex-col min-w-0 min-h-0 lg:min-h-0 ${showScheduleLoading ? 'overflow-hidden' : 'overflow-y-auto'}`}
                    >
                        {/* Schedule Area — natural height so scrollContainerRef can scroll; avoid flex-grow+overflow-hidden clipping content */}
                        <div className={`min-w-0 ${showScheduleLoading ? 'flex-1 min-h-0' : ''}`}>
                            {!showScheduleLoading && (timeMode === 'events' ? (
                                <ScheduleEventsView
                                    canViewMeetings={canViewMeetings}
                                    eventPanelEntryType={eventPanelEntryType}
                                    onEventPanelEntryTypeChange={setEventPanelEntryType}
                                    baseScheduleItems={baseScheduleItems}
                                    currentDate={currentDate}
                                    isAdmin={false}
                                    isPublicView={isPublicView}
                                    onOpenScheduleModal={openScheduleModal}
                                    groups={groups}
                                    subjects={subjects}
                                    teachers={teachers}
                                    rooms={rooms}
                                    activeDays={activeDays}
                                />
                            ) : timeMode === 'occupancy' ? (
                                <RoomsOccupancyView
                                    rooms={rooms || []}
                                    scheduleEntries={baseScheduleItems || []}
                                    selectedPeriod={selectedPeriod}
                                    activeDays={activeDays}
                                    holidays={weekHolidays}
                                    onRoomClick={(room) => console.log('Room clicked:', room)}
                                    onTimeSlotClick={(entry, room, dayIndex) => openScheduleModal(entry)}
                                />
                            ) : useLinearFilteredView ? (
                                <LinearWeekViewDOM
                                    items={filteredScheduleItems}
                                    days={activeDays}
                                    rooms={rooms}
                                    onOpenModal={openScheduleModal}
                                    user={user}
                                    currentWeekStart={getWeekStartDate(currentDate)}
                                    selectedTeacher={selectedTeacher}
                                    selectedGroups={selectedGroups}
                                    onNextWeek={handleNextWeek}
                                />
                            ) : (
                                viewMode === 'wide' ? (
                                    <SharedScrollProvider>
                                        <div className="space-y-6">
                                            {activeDays.map(day => {
                                                const itemsForDay = filteredScheduleItems.filter(item => item && item.day === day);

                                                // Use time-mode base entries instead of recalculating from base
                                                const dayScheduleEntry = (timeMode === 'semester' && selectedPeriod
                                                    ? semesterEntries
                                                    : weekActiveEntries).filter(entry => {
                                                        if (!entry || entry.day !== day) return false;

                                                        // Already filtered for current mode
                                                        return true;
                                                    });

                                                return (
                                                    <div
                                                        key={`${day}-wide-container`}
                                                        ref={el => {
                                                            if (el) {
                                                                wideDaySectionRefs.current[day] = el;
                                                            } else {
                                                                delete wideDaySectionRefs.current[day];
                                                            }
                                                        }}
                                                        className="w-full overflow-hidden"
                                                    >
                                                        <DayTableSimple
                                                            key={`${day}-wide`}
                                                            day={day}
                                                            rooms={(complexFilterRooms.length > 1 ? filteredRoomsForColumns : (rooms || []))}
                                                            items={itemsForDay}
                                                            dayScheduleEntry={dayScheduleEntry}
                                                            user={user}
                                                            teachers={teachers}
                                                            groups={groups}
                                                            subjects={subjects}
                                                            onOpenModal={openScheduleModal}
                                                            onItemDrop={noop}
                                                            draggedItemInfo={EMPTY_DRAGGED_ITEM_INFO}
                                                            onGlobalDragStart={noop}
                                                            onGlobalDragEnd={noop}
                                                            KEY_SEPARATOR={KEY_SEPARATOR}
                                                            dayHeaderRef={element => dayHeaderRefs.current[day] = element}
                                                            currentWeekNumber={targetWeekNumber}
                                                            currentYear={targetYear}
                                                            isPublicView={isPublicView}
                                                            holiday={weekHolidays.get(day) || null}
                                                            isToday={day === todayWideDayKey}
                                                            readOnly
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SharedScrollProvider>
                                ) : (
                                    <SharedScrollProvider>
                                        <div ref={fitViewContainerRef} className="flex flex-col w-full border border-gray-300 bg-white shadow-lg" style={{ height: `${fitViewHeight}px` }}>
                                            {activeDays.map((day, index) => {
                                                const itemsForDay = filteredScheduleItems.filter(item => item && item.day === day);
                                                const dayTableFitHeaderHeight = 40; // Approx header height in DayTableFit
                                                const contentHeightForChild = Math.max(1, fitViewHeight / activeDays.length - dayTableFitHeaderHeight);

                                                // Use time-mode base entries instead of recalculating from base
                                                const dayScheduleEntry = (timeMode === 'semester' && selectedPeriod
                                                    ? semesterEntries
                                                    : weekActiveEntries).filter(entry => {
                                                        if (!entry || entry.day !== day) return false;

                                                        // Already filtered for current mode
                                                        return true;
                                                    });

                                                return (
                                                    <div key={`${day}-fit-wrapper`} className={`flex-grow ${index < activeDays.length - 1 ? 'border-b border-gray-200' : ''}`} style={{ minHeight: 0 }}>
                                                        <DayTableFit
                                                            key={`${day}-fit`} day={day} rooms={roomsToUseFit} items={itemsForDay}
                                                            dayScheduleEntry={dayScheduleEntry} user={user}
                                                            teachers={teachers}
                                                            groups={groups}
                                                            subjects={subjects}
                                                            currentWeekNumber={targetWeekNumber}
                                                            currentYear={targetYear}
                                                            onOpenModal={openScheduleModal}
                                                            availableContentHeight={contentHeightForChild}
                                                            onItemDrop={noop}
                                                            draggedItemInfo={EMPTY_DRAGGED_ITEM_INFO}
                                                            onGlobalDragStart={noop}
                                                            onGlobalDragEnd={noop}
                                                            KEY_SEPARATOR={KEY_SEPARATOR}
                                                            isPublicView={isPublicView}
                                                            holiday={weekHolidays.get(day) || null}
                                                            readOnly
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SharedScrollProvider>
                                )
                            ))}
                        </div>

                        <footer className={`bg-gray-50 text-center text-xs text-gray-300 p-1 shrink-0 ${showScheduleLoading ? 'mt-auto' : 'mt-6'}`}>
                            © 2025 Pi Schedule by Baptiste Bohelay. All rights reserved.
                        </footer>
                    </div>

                    {/* Events/Meetings Panel - hidden in occupancy mode and on mobile */}
                    {timeMode !== 'occupancy' && timeMode !== 'events' && !isMobileLayout && (
                        <div
                            className={`flex flex-col gap-0 transition-all duration-300 flex-shrink-0 min-h-0 lg:h-full lg:max-h-full ${isEventPanelCollapsed ? 'w-10' : 'w-full lg:w-60 xl:w-[250px]'}`}
                        >
                            <div className="flex w-full rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setIsEventPanelCollapsed(prev => !prev)}
                                    className={`px-2 sm:px-3 py-2 text-sm flex items-center justify-center gap-1 transition-colors border-r border-gray-200 text-gray-600 hover:bg-gray-50 ${isEventPanelCollapsed ? 'bg-blue-50 text-blue-700' : ''}`}
                                    aria-label={isEventPanelCollapsed ? 'Open events panel' : 'Close events panel'}
                                    aria-expanded={!isEventPanelCollapsed}
                                >
                                    {isEventPanelCollapsed ? (
                                        <HiChevronLeft className="w-4 h-4" />
                                    ) : (
                                        <HiChevronRight className="w-4 h-4" />
                                    )}
                                </button>

                                {!isEventPanelCollapsed && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setEventPanelEntryType('event')}
                                            className={`flex-1 min-w-0 whitespace-nowrap px-3 py-2 text-sm flex items-center justify-center gap-2 transition-colors ${canViewMeetings && effectiveEntryType === 'event'
                                                ? 'bg-blue-50 text-blue-700 font-semibold'
                                                : 'text-gray-600 hover:bg-gray-50'}`}
                                            aria-pressed={canViewMeetings ? effectiveEntryType === 'event' : undefined}
                                        >
                                            <IoMdMicrophone className="w-4 h-4" />
                                            Events
                                        </button>
                                        {canViewMeetings && (
                                            <button
                                                type="button"
                                                onClick={() => setEventPanelEntryType('meeting')}
                                                className={`flex-1 min-w-0 whitespace-nowrap px-3 py-2 text-sm flex items-center justify-center gap-2 transition-colors border-l border-gray-200 ${effectiveEntryType === 'meeting'
                                                    ? 'bg-blue-50 text-blue-700 font-semibold'
                                                    : 'text-gray-600 hover:bg-gray-50'}`}
                                                aria-pressed={effectiveEntryType === 'meeting'}
                                            >
                                                <FaRegClock className="w-4 h-4" />
                                                Meetings
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>

                            {!isEventPanelCollapsed && (
                                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                    <EventContainer
                                        baseScheduleItems={baseScheduleItems}
                                        currentDate={currentDate}
                                        isAdmin={false}
                                        isPublicView={isPublicView}
                                        onOpenScheduleModal={openScheduleModal}
                                        groups={groups}
                                        subjects={subjects}
                                        teachers={teachers}
                                        rooms={rooms}
                                        activeDays={activeDays}
                                        entryType={effectiveEntryType}
                                        panelTitle={effectiveEntryType === 'meeting' ? 'Upcoming Meetings' : 'Upcoming Events'}
                                        emptyMessage={effectiveEntryType === 'meeting' ? 'No upcoming meetings.' : 'No upcoming events.'}
                                        addButtonLabel={effectiveEntryType === 'meeting' ? 'Add New Meeting' : 'Add New Event'}
                                        documentationId={effectiveEntryType === 'meeting' ? 'meetings' : 'events'}
                                        showTitle={false}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Wide / fit view toggle */}
            {!isFilteredView && timeMode !== 'occupancy' && timeMode !== 'events' && (
                <div className={`${isMobileLayout ? 'hidden' : 'flex'} fixed bottom-6 right-0 z-50`}>
                    <Button
                        onClick={toggleViewMode}
                        variant="secondary" // Or another appropriate variant
                        className="rounded-full p-2 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center w-14 h-14 mr-6 bg-white text-gray-700 hover:bg-gray-50 ring-gray-400" // Style like other FABs
                        title={viewMode === 'wide' ? 'Switch to Fit View' : 'Switch to Wide View'}
                        aria-label={viewMode === 'wide' ? 'Switch to Fit View' : 'Switch to Wide View'}
                    >
                        {/* Conditionally render the icon based on the CURRENT view */}
                        {viewMode === 'wide' ? <FitIcon /> : <ExpandIcon />}
                    </Button>
                </div>
            )}

            {/* Teacher build: mobile advanced filters (ComplexFilterModal); desktop uses ScheduleFilter toolbar */}
            {isTeachersBuild() && timeMode !== 'occupancy' && timeMode !== 'events' && (
                <div className={`${isMobileLayout ? 'fixed' : 'hidden'} bottom-1 right-1 z-50`}>
                    <Button
                        onClick={handleOpenComplexFilter}
                        variant="ghost"
                        className="!rounded-full !p-0 aspect-square w-14 h-14 min-w-[3.5rem] min-h-[3.5rem] flex items-center justify-center border-2 bg-white text-blue-700 shadow-[0_6px_24px_-4px_rgba(0,0,0,0.05),0_2px_8px_-2px_rgba(15,23,42,0.12)] hover:!bg-blue-100 hover:border-blue-400 hover:text-blue-900 hover:shadow-[0_8px_28px_-4px_rgba(37,99,235,0.4),0_2px_10px_-2px_rgba(15,23,42,0.14)] focus-visible:!ring-2 focus-visible:!ring-blue-500 focus-visible:!ring-offset-2"
                        title="Advanced filters"
                        aria-label="Open advanced filters"
                    >
                        <HiAdjustmentsHorizontal className="h-6 w-6" />
                    </Button>
                </div>
            )}

            <SchedulePrimaryViewDrawer
                isOpen={isLeftDrawerOpen}
                isAnimating={isLeftDrawerAnimating}
                onClose={closeLeftDrawer}
                timeMode={timeMode}
                onSelectPrimaryView={handleSelectPrimaryView}
                onChangeInstitution={isPublicView ? handleChangeInstitution : undefined}
            />

            <ScheduleEntryDetailView
                isOpen={isDetailViewOpen}
                onClose={closeDetailView}
                scheduleEntry={selectedScheduleItem}
                groups={groups}
                subjects={subjects}
                teachers={teachers}
                rooms={rooms}
                isPublic={isPublicView}
            />

            <ComplexFilterModal
                isOpen={isComplexFilterModalOpen}
                onClose={handleCloseComplexFilter}
                onApplyFilters={handleApplyComplexFilters}
                groups={groups}
                teachers={teachers}
                subjects={subjects}
                rooms={rooms}
                initialSelectedGroups={complexFilterGroups}
                initialSelectedTeachers={complexFilterTeachers}
                initialSelectedSubjects={complexFilterSubjects}
                initialSelectedRooms={complexFilterRooms}
            />

        </div> // End main container div
    );
}

export default LiveScheduleView;
